import { useEffect, useMemo, useState } from "react";
import { UserRound, Pencil, User, Shield, Key, Clock, Settings } from "lucide-react";
import { AppShell } from "../layouts/AppShell";
import { EntityWorkspace } from "../layouts/EntityWorkspace";
import { EntityListSidebar } from "../layouts/EntityListSidebar";
import { EntityHeaderPattern } from "../patterns/EntityHeaderPattern";
import { Modal } from "../primitives/Modal";
import { Drawer } from "../primitives/Drawer";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { Checkbox } from "../primitives/Checkbox";
import { Button } from "../primitives/Button";
import { IconButton } from "../primitives/IconButton";
import { Tabs } from "../primitives/Tabs";
import { MembershipsTable } from "../components/MembershipsTable";
import { EditMembershipDrawer } from "../components/EditMembershipDrawer";
import { AddMembershipModal } from "../components/AddMembershipModal";
import { ChangeAmbassadorFlow } from "../components/ChangeAmbassadorFlow";
import { NotesList } from "../components/NotesList";
import { AuditLogList } from "../components/AuditLogList";
import {
  ROLE_OPTIONS,
  createAdminUser,
  inviteAdminUser,
  listAdminUsers,
  listAmbassadorOrgs,
  listUserMemberships,
  updateAdminUser,
  updateAdminUserDetails,
  updateMembership,
  createMembership,
  getAdminOrgId,
  disableMembership,
  deleteAdminUser,
  listAuditLogs,
} from "../../api/admin";
import { requestPasswordReset } from "../../api/auth";
import type { PermissionOverride } from "../../utils/permissions";
import { getRoleDescription } from "../../utils/permissions";

type AdminUser = {
  id: string;
  email: string;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  phone?: string | null;
  status: string;
  is_superadmin: boolean;
  invited_at?: string | null;
  invite_count?: number;
  last_invite_sent_at?: string | null;
  password_changed_at?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  province?: string | null;
  postal_code?: string | null;
  country?: string | null;
};

type AmbassadorOrg = {
  id: string;
  name: string;
};

export function AdminUsersPage({ userName = "Utilisateur", isSuperAdmin = false }: { userName?: string; isSuperAdmin?: boolean }) {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [ambassadors, setAmbassadors] = useState<AmbassadorOrg[]>([]);
  const [memberships, setMemberships] = useState<
    {
      id: string;
      organization_id: string;
      organization?: { name?: string; type?: string };
      org_role: string | null;
      custom_role_id: string | null;
      status: string;
      permissions_override?: PermissionOverride | null;
      custom_role?: { id: string; name: string; description?: string | null } | null;
    }[]
  >([]);
  const [editingMembership, setEditingMembership] = useState<{
    id: string;
    organization_id: string;
    organization?: { name?: string; type?: string };
    org_role: string | null;
    custom_role_id: string | null;
    status: string;
    permissions_override?: PermissionOverride | null;
    custom_role?: { id: string; name: string; description?: string | null } | null;
  } | null>(null);
  const [addMembershipOpen, setAddMembershipOpen] = useState(false);
  const [changeAmbassadorOpen, setChangeAmbassadorOpen] = useState(false);
  const [adminOrgId, setAdminOrgId] = useState<string>("");
  const [open, setOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<string>("profile");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editingFields, setEditingFields] = useState({
    email: "",
    firstName: "",
    lastName: "",
    phone: "",
    isSuperadmin: false,
    address_line1: "",
    address_line2: "",
    city: "",
    province: "",
    postal_code: "",
    country: "",
  });
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [isLoadingAuditLogs, setIsLoadingAuditLogs] = useState(false);
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    role: "ops",
    ambassadorId: "",
    ambassadorName: "Aucun",
    // isSuperadmin removed - not needed in creation form
  });

  useEffect(() => {
    let mounted = true;
    listAdminUsers()
      .then((data) => {
        if (!mounted) return;
        setUsers(data.items || []);
        if (data.items?.[0]?.id) {
          setSelectedId(data.items[0].id);
        }
      })
      .catch(() => undefined);
    listAmbassadorOrgs()
      .then((data) => {
        if (!mounted) return;
        setAmbassadors(data.items || []);
      })
      .catch(() => undefined);
    getAdminOrgId()
      .then((id) => {
        if (!mounted) return;
        setAdminOrgId(id);
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, []);

  // Close edit mode when switching users (but not when switching tabs)
  useEffect(() => {
    if (selectedId) {
      setIsEditingProfile(false);
      const user = users.find((u) => u.id === selectedId);
      if (user) {
        setEditingFields({
          email: user.email || "",
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          phone: user.phone || "",
          isSuperadmin: user.is_superadmin || false,
          address_line1: user.address_line1 || "",
          address_line2: user.address_line2 || "",
          city: user.city || "",
          province: user.province || "",
          postal_code: user.postal_code || "",
          country: user.country || "",
        });
      } else {
        setEditingFields({
          email: "",
          firstName: "",
          lastName: "",
          phone: "",
          isSuperadmin: false,
          address_line1: "",
          address_line2: "",
          city: "",
          province: "",
          postal_code: "",
          country: "",
        });
      }
    }
  }, [selectedId, users]);

  useEffect(() => {
    if (!selectedId) return;
    let mounted = true;
    listUserMemberships(selectedId)
      .then((data) => {
        if (!mounted) return;
        setMemberships(data.items || []);
        const admin = (data.items || []).find((item: { organization_id: string }) => item.organization_id === adminOrgId);
        if (admin?.org_role) {
          setForm((prev) => ({ ...prev, role: admin.org_role }));
        }
        const ambassador = (data.items || []).find(
          (item: { organization_id: string }) => item.organization_id !== adminOrgId
        );
        if (ambassador?.organization?.name) {
          setForm((prev) => ({
            ...prev,
            ambassadorName: ambassador.organization?.name ?? "Aucun",
            ambassadorId: ambassador.organization_id,
          }));
        }
      })
      .catch(() => undefined);
    return () => {
      mounted = false;
    };
  }, [selectedId, adminOrgId]);

  useEffect(() => {
    if (!selectedId || activeTab !== "history") return;
    let mounted = true;
    setIsLoadingAuditLogs(true);
    listAuditLogs({
      object_type: "user",
      object_id: selectedId,
      page_size: 50,
    })
      .then((data) => {
        if (!mounted) return;
        setAuditLogs(data.items || []);
        setIsLoadingAuditLogs(false);
      })
      .catch(() => {
        if (!mounted) return;
        setIsLoadingAuditLogs(false);
      });
    return () => {
      mounted = false;
    };
  }, [selectedId, activeTab]);

  const selectedUser = useMemo(
    () => users.find((user) => user.id === selectedId) || null,
    [users, selectedId]
  );

  const adminMembership = useMemo(() => {
    if (!adminOrgId) return null;
    return memberships.find((membership) => membership.organization_id === adminOrgId) || null;
  }, [memberships, adminOrgId]);

  const ambassadorMembership = useMemo(() => {
    return memberships.find((membership) => membership.organization_id !== adminOrgId) || null;
  }, [memberships, adminOrgId]);

  // Helper function to determine if invitation is pending
  const isInvitationPending = (user: AdminUser): boolean => {
    // An invitation is pending if the user was invited but hasn't accepted yet
    // A user has accepted if password_changed_at exists (they set their password)
    if (user.password_changed_at) return false; // User has accepted invitation
    if (!user.invited_at) return false; // Never invited
    // If invited but no password_changed_at, invitation is still pending
    return true;
  };

  const listItems = useMemo(
    () =>
      users.map((user) => {
        const pendingInvite = isInvitationPending(user);
        // Determine status label: if user has no password (invited but not accepted), show "Invité"
        const statusLabel = pendingInvite 
          ? "Invité" 
          : (user.status === "active" ? "Actif" : user.status === "suspended" ? "Suspendu" : user.status);
        return {
          id: user.id,
          title: user.display_name || `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.email,
          subtitle: user.email,
          info: user.is_superadmin ? "SuperAdmin" : user.status,
          icon: <UserRound size={16} strokeWidth={1.5} />,
          status: { 
            label: statusLabel, 
            tone: pendingInvite ? "warning" : (user.status === "active" ? "success" : "warning") 
          },
        };
      }),
    [users]
  );

  const handleCreate = async () => {
    // Validation des champs requis
    const newErrors: Record<string, string> = {};
    if (!form.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = "Veuillez entrer une adresse email valide";
    }
    if (!form.firstName.trim()) {
      newErrors.firstName = "Le prénom est requis";
    }
    if (!form.lastName.trim()) {
      newErrors.lastName = "Le nom est requis";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setErrors({});
    try {
      const emailToCreate = form.email; // Save email before resetting form
    const created = await createAdminUser({
      email: form.email,
      first_name: form.firstName,
      last_name: form.lastName,
      role: form.role,
      ambassador_org_id: form.ambassadorId || undefined,
      // is_superadmin removed - not sent on initial creation
    });
      // Reset form
      setForm({
        email: "",
        firstName: "",
        lastName: "",
        role: "ops",
        ambassadorId: "",
        ambassadorName: "Aucun",
        // isSuperadmin removed from form state
      });
      setOpen(false);
      setErrors({});
      
      // Show invitation status message
      if (created?.invite_status === "invited" || created?.invite_status === "sent") {
        alert(`Utilisateur créé avec succès. Un courriel d'invitation a été envoyé à ${emailToCreate}.`);
      } else if (created?.invite_status === "failed") {
        alert(`Utilisateur créé, mais l'envoi de l'invitation a échoué. Vous pouvez réenvoyer l'invitation depuis le profil de l'utilisateur.`);
      } else {
        alert("Utilisateur créé avec succès.");
      }
      
      // Refresh the list to ensure consistency
      const data = await listAdminUsers();
      setUsers(data.items || []);
      if (data.items && data.items.length > 0) {
        // Select the newly created user
        const newUser = data.items.find((u: AdminUser) => u.email === emailToCreate);
        if (newUser) {
          setSelectedId(newUser.id);
        } else if (created?.user?.id) {
    setSelectedId(created.user.id);
        } else {
          setSelectedId(data.items[0].id);
        }
      }
    } catch (error) {
      console.error("Failed to create user:", error);
      alert("Erreur lors de la création de l'utilisateur. Veuillez réessayer.");
    }
  };

  const handleCancel = () => {
    setForm({
      email: "",
      firstName: "",
      lastName: "",
      role: "ops",
      ambassadorId: "",
      ambassadorName: "Aucun",
    });
    setErrors({});
    setOpen(false);
  };

  const handleToggleStatus = async () => {
    if (!selectedUser) return;
    const nextStatus = selectedUser.status === "active" ? "suspended" : "active";
    const updated = await updateAdminUser(selectedUser.id, { status: nextStatus });
    setUsers((prev) => prev.map((user) => (user.id === selectedUser.id ? updated : user)));
  };

  const handleSuspendUser = async (userId: string) => {
    try {
      const updated = await updateAdminUser(userId, { status: "suspended" });
      setUsers((prev) => prev.map((user) => (user.id === userId ? updated : user)));
      // Refresh the list to ensure consistency
      const data = await listAdminUsers();
      setUsers(data.items || []);
    } catch (error) {
      console.error("Failed to suspend user:", error);
      alert("Erreur lors de la suspension de l'utilisateur. Veuillez réessayer.");
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer définitivement cet utilisateur ? Cette action est irréversible et l'utilisateur ne sera plus visible dans l'interface.")) {
      return;
    }
    try {
      await deleteAdminUser(userId);
      // Refresh the list (archived users are filtered out)
      const data = await listAdminUsers();
      setUsers(data.items || []);
      if (selectedId === userId) {
        setSelectedId(null);
      }
      alert("Utilisateur supprimé avec succès.");
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert(`Erreur lors de la suppression de l'utilisateur: ${(error as Error).message || "Veuillez réessayer."}`);
    }
  };

  const handleInvite = async () => {
    if (!selectedUser) return;
    // Check if user has already accepted invitation (has password_changed_at or is active without invited_at)
    const hasAcceptedInvitation = selectedUser.password_changed_at || (selectedUser.status === "active" && !selectedUser.invited_at);
    
    if (hasAcceptedInvitation) {
      // User has already accepted, send password reset instead
      try {
        await requestPasswordReset(selectedUser.email);
        setSuccessMessage(`Un courriel de réinitialisation de mot de passe a été envoyé à ${selectedUser.email}.`);
        setTimeout(() => setSuccessMessage(null), 5000);
      } catch (error) {
        console.error("Failed to send password reset:", error);
        setSuccessMessage(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    } else {
      // User hasn't accepted yet, send invitation
      try {
        const result = await inviteAdminUser(selectedUser.id);
        if (result?.status === "invited") {
          setSuccessMessage(`Invitation envoyée avec succès à ${selectedUser.email}.`);
          setTimeout(() => setSuccessMessage(null), 5000);
          // Refresh the user list to update invitation status
          const data = await listAdminUsers();
          setUsers(data.items || []);
        } else if (result?.status === "skipped") {
          setSuccessMessage("Invitation ignorée: l'envoi est désactivé en configuration actuelle.");
          setTimeout(() => setSuccessMessage(null), 5000);
        } else {
          setSuccessMessage("Invitation traitée.");
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      } catch (error) {
        console.error("Failed to invite user:", error);
        setSuccessMessage(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
        setTimeout(() => setSuccessMessage(null), 5000);
      }
    }
  };

  const handleEditProfile = async () => {
    if (!selectedUser) return;
    const updated = await updateAdminUserDetails(selectedUser.id, {
      email: form.email,
      first_name: form.firstName,
      last_name: form.lastName,
      display_name: `${form.firstName} ${form.lastName}`.trim(),
      phone: form.phone,
    });
    setUsers((prev) => prev.map((user) => (user.id === selectedUser.id ? updated : user)));
    setEditDrawerOpen(false);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser) return;
    if (adminMembership) {
      await updateMembership(adminMembership.id, { org_role: form.role });
    } else if (adminOrgId) {
      await createMembership({ organization_id: adminOrgId, user_id: selectedUser.id, org_role: form.role });
    }
    if (form.ambassadorId) {
      if (ambassadorMembership) {
        await updateMembership(ambassadorMembership.id, { org_role: "ops" });
      } else {
        await createMembership({ organization_id: form.ambassadorId, user_id: selectedUser.id, org_role: "ops" });
      }
    }
    setEditDrawerOpen(false);
  };

  // Populate edit form when opening the edit drawer
  useEffect(() => {
    if (!editDrawerOpen || !selectedUser) return;
    setForm((prev) => ({
      ...prev,
      email: selectedUser.email || "",
      firstName: selectedUser.first_name || "",
      lastName: selectedUser.last_name || "",
      phone: selectedUser.phone || "",
    }));
  }, [editDrawerOpen, selectedUser]);

  return (
    <AppShell
      topbar={<div className="text-sm text-muted">Utilisateurs</div>}
      userName={userName}
      containerFluid={true}
      modules={[
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        { label: "Clients", onClick: () => (window.location.hash = "#/clients") },
        { label: "Ambassadeurs", onClick: () => (window.location.hash = "#/ambassadors") },
        { label: "Pilotes", onClick: () => (window.location.hash = "#/pilots") },
        { label: "Catalogue", onClick: () => (window.location.hash = "#/admin-products"), separator: true },
        { label: "Inventaire", onClick: () => (window.location.hash = "#/inventories") },
        { label: "Soumission", onClick: () => (window.location.hash = "#/soumission") },
        { label: "Administration", sectionHeader: true, separator: true },
        { label: "Utilisateurs", onClick: () => (window.location.hash = "#/admin") },
        { label: "Organisations", onClick: () => (window.location.hash = "#/admin-orgs") },
        { label: "Services et Produits", onClick: () => (window.location.hash = "#/admin-products") },
        { label: "Promotions", onClick: () => (window.location.hash = "#/admin-promotions") },
        { label: "Processus", onClick: () => (window.location.hash = "#/admin-processes") },
        { label: "Templates de courriel", onClick: () => (window.location.hash = "#/admin-email-templates") },
        { label: "Facturation", onClick: () => (window.location.hash = "#/admin-billing") },
      ]}
    >
      <EntityWorkspace
        header={
          selectedUser ? (
            <EntityHeaderPattern
              title={selectedUser.display_name || selectedUser.email}
              metaItems={[
                { label: "Statut", value: selectedUser.status, tone: "default" },
                { label: "Rôle", value: selectedUser.is_superadmin ? "SuperAdmin" : "Utilisateur", tone: "muted" },
              ]}
              secondaryActions={[
                { label: "Modifier", onClick: () => setEditDrawerOpen(true) },
                ...(isInvitationPending(selectedUser) 
                  ? [{ label: "Réenvoyer l'invitation", onClick: handleInvite }]
                  : [{ label: "Inviter", onClick: handleInvite }]
                ),
                {
                  label: selectedUser.status === "active" ? "Suspendre" : "Réactiver",
                  onClick: handleToggleStatus,
                },
                ...(isSuperAdmin ? [{
                  label: "Supprimer",
                  onClick: () => handleDeleteUser(selectedUser.id),
                }] : []),
              ]}
            />
          ) : null
        }
        sidebarTitle="Utilisateurs"
        sidebarIcon="👤"
        sidebarMeta={users.length ? `${users.length}` : undefined}
        sidebarItems={[]}
        storageKey="xlflow.admin.users.sidebar"
        allowHoverOpen
        renderSidebar={(isExpanded) => (
          <EntityListSidebar
            sections={[]}
            items={listItems}
            selectedId={selectedId}
            onSelect={(id) => setSelectedId(id)}
            isExpanded={isExpanded}
            searchPlaceholder="Rechercher un utilisateur"
            filterOptions={["Tous", "Actif", "Suspendu"]}
            emptyMessage="Aucun utilisateur disponible."
            addActionLabel="+ Ajouter"
            onAddAction={() => setOpen(true)}
          />
        )}
        content={
          selectedUser ? (
            <div className="space-y-4">
              <Tabs
                tabs={[
                  { id: "profile", label: "Profil" },
                  { id: "access", label: "Accès & Permissions" },
                  { id: "notes", label: "Notes" },
                  { id: "history", label: "Historique" },
                  { id: "settings", label: "Paramètres" },
                ]}
                activeId={activeTab}
                onChange={(newTab) => {
                  // Don't close edit mode when switching tabs, only when switching users
                  setActiveTab(newTab);
                }}
                variant="underline"
              />
              {activeTab === "profile" && (
                <div className="space-y-6">
                  {successMessage && (
                    <div className="p-3 rounded-md border border-success/30 bg-success/10 text-success text-sm">
                      {successMessage}
                    </div>
                  )}
                  
                  {/* Section 1: Informations générales */}
                  <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-semibold text-text">Informations générales</h3>
                      {!isEditingProfile && (
                        <button
                          onClick={() => {
                            setIsEditingProfile(true);
                            setEditingFields({
                              email: selectedUser.email || "",
                              firstName: selectedUser.first_name || "",
                              lastName: selectedUser.last_name || "",
                              phone: selectedUser.phone || "",
                              isSuperadmin: selectedUser.is_superadmin,
                            });
                          }}
                          className="text-xs text-primary hover:text-primary/80 font-medium"
                        >
                          Modifier
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {/* Ligne 1: Prénom et Nom */}
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-xs text-muted">Prénom</label>
                          {isEditingProfile ? (
                            <Input
                              value={editingFields.firstName}
                              onChange={(e) => setEditingFields({ ...editingFields, firstName: e.target.value })}
                              className="mt-1"
                            />
                          ) : (
                            <div className="mt-1 text-sm text-text">{selectedUser.first_name || "—"}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-muted">Nom</label>
                          {isEditingProfile ? (
                            <Input
                              value={editingFields.lastName}
                              onChange={(e) => setEditingFields({ ...editingFields, lastName: e.target.value })}
                              className="mt-1"
                            />
                          ) : (
                            <div className="mt-1 text-sm text-text">{selectedUser.last_name || "—"}</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Ligne 2: Email et Téléphone */}
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="text-xs text-muted">Email</label>
                          {isEditingProfile ? (
                            <Input
                              value={editingFields.email}
                              onChange={(e) => setEditingFields({ ...editingFields, email: e.target.value })}
                              className="mt-1"
                              placeholder="email@exemple.com"
                            />
                          ) : (
                            <div className="mt-1 text-sm text-text">{selectedUser.email}</div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-muted">Téléphone</label>
                          {isEditingProfile ? (
                            <Input
                              value={editingFields.phone}
                              onChange={(e) => setEditingFields({ ...editingFields, phone: e.target.value })}
                              className="mt-1"
                              placeholder="(xxx) xxx-xxxx"
                            />
                          ) : (
                            <div className="mt-1 text-sm text-text">{selectedUser.phone || "—"}</div>
                          )}
                        </div>
                      </div>
                      
                      {/* Ligne 3: Type */}
                      <div>
                        <label className="text-xs text-muted">Type</label>
                        {isEditingProfile && isSuperAdmin ? (
                          <div className="mt-1">
                            <Checkbox
                              label="SuperAdmin"
                              checked={editingFields.isSuperadmin}
                              onChange={(checked) => setEditingFields({ ...editingFields, isSuperadmin: checked })}
                            />
                          </div>
                        ) : (
                          <div className="mt-1">
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                selectedUser.is_superadmin ? "bg-primary/10 text-primary" : "bg-muted/10 text-muted"
                              }`}
                            >
                              {selectedUser.is_superadmin ? "SuperAdmin" : "Utilisateur"}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {/* Section Adresse */}
                      <div className="pt-4 mt-4 border-t border-border">
                        <h4 className="text-xs font-semibold text-text mb-3">Adresse</h4>
                        <div className="space-y-4">
                          <div>
                            <label className="text-xs text-muted">Adresse</label>
                            {isEditingProfile ? (
                              <Input
                                value={editingFields.address_line1}
                                onChange={(e) => setEditingFields({ ...editingFields, address_line1: e.target.value })}
                                className="mt-1"
                                placeholder="123 rue Principale"
                              />
                            ) : (
                              <div className="mt-1 text-sm text-text">{selectedUser.address_line1 || "—"}</div>
                            )}
                          </div>
                          
                          <div>
                            <label className="text-xs text-muted">Complément d'adresse</label>
                            {isEditingProfile ? (
                              <Input
                                value={editingFields.address_line2}
                                onChange={(e) => setEditingFields({ ...editingFields, address_line2: e.target.value })}
                                className="mt-1"
                                placeholder="Appartement, bureau, etc."
                              />
                            ) : (
                              <div className="mt-1 text-sm text-text">{selectedUser.address_line2 || "—"}</div>
                            )}
                          </div>
                          
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <label className="text-xs text-muted">Ville</label>
                              {isEditingProfile ? (
                                <Input
                                  value={editingFields.city}
                                  onChange={(e) => setEditingFields({ ...editingFields, city: e.target.value })}
                                  className="mt-1"
                                  placeholder="Montréal"
                                />
                              ) : (
                                <div className="mt-1 text-sm text-text">{selectedUser.city || "—"}</div>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-muted">Code postal</label>
                              {isEditingProfile ? (
                                <Input
                                  value={editingFields.postal_code}
                                  onChange={(e) => setEditingFields({ ...editingFields, postal_code: e.target.value })}
                                  className="mt-1"
                                  placeholder="H1A 1A1"
                                />
                              ) : (
                                <div className="mt-1 text-sm text-text">{selectedUser.postal_code || "—"}</div>
                              )}
                            </div>
                          </div>
                          
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <label className="text-xs text-muted">Province</label>
                              {isEditingProfile ? (
                                <Input
                                  value={editingFields.province}
                                  onChange={(e) => setEditingFields({ ...editingFields, province: e.target.value })}
                                  className="mt-1"
                                  placeholder="QC"
                                />
                              ) : (
                                <div className="mt-1 text-sm text-text">{selectedUser.province || "—"}</div>
                              )}
                            </div>
                            <div>
                              <label className="text-xs text-muted">Pays</label>
                              {isEditingProfile ? (
                                <Input
                                  value={editingFields.country}
                                  onChange={(e) => setEditingFields({ ...editingFields, country: e.target.value })}
                                  className="mt-1"
                                  placeholder="CA"
                                />
                              ) : (
                                <div className="mt-1 text-sm text-text">{selectedUser.country || "—"}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    {isEditingProfile && (
                      <div className="pt-4 mt-4 border-t border-border flex justify-end gap-2">
                        <Button
                          label="Annuler"
                          intent="default"
                          size="sm"
                          onClick={() => {
                            setIsEditingProfile(false);
                            const user = users.find((u) => u.id === selectedId);
                            if (user) {
                              setEditingFields({
                                email: user.email || "",
                                firstName: user.first_name || "",
                                lastName: user.last_name || "",
                                phone: user.phone || "",
                                isSuperadmin: user.is_superadmin || false,
                                address_line1: user.address_line1 || "",
                                address_line2: user.address_line2 || "",
                                city: user.city || "",
                                province: user.province || "",
                                postal_code: user.postal_code || "",
                                country: user.country || "",
                              });
                            }
                          }}
                        />
                        <Button
                          label="Enregistrer"
                          intent="primary"
                          size="sm"
                          onClick={async () => {
                            try {
                              // Validate email format
                              if (!editingFields.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editingFields.email)) {
                                setSuccessMessage("Veuillez entrer une adresse email valide.");
                                setTimeout(() => setSuccessMessage(null), 5000);
                                return;
                              }
                              await updateAdminUserDetails(selectedUser.id, {
                                email: editingFields.email,
                                first_name: editingFields.firstName,
                                last_name: editingFields.lastName,
                                phone: editingFields.phone,
                                is_superadmin: editingFields.isSuperadmin,
                                address_line1: editingFields.address_line1 || null,
                                address_line2: editingFields.address_line2 || null,
                                city: editingFields.city || null,
                                province: editingFields.province || null,
                                postal_code: editingFields.postal_code || null,
                                country: editingFields.country || null,
                              });
                              setSuccessMessage("Profil mis à jour avec succès.");
                              setTimeout(() => setSuccessMessage(null), 5000);
                              setIsEditingProfile(false);
                              const data = await listAdminUsers();
                              setUsers(data.items || []);
                            } catch (error) {
                              setSuccessMessage(`Erreur: ${(error as Error).message || "Veuillez réessayer."}`);
                              setTimeout(() => setSuccessMessage(null), 5000);
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Section 2: Statut */}
                  <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
                    <h3 className="text-sm font-semibold text-text mb-4">Statut</h3>
                    {selectedUser.invited_at ? (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <label className="text-xs text-muted mb-1 block">Statut d'invitation</label>
                            <span
                              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                                isInvitationPending(selectedUser)
                                  ? "bg-warning/10 text-warning"
                                  : "bg-success/10 text-success"
                              }`}
                            >
                              {isInvitationPending(selectedUser) ? "En attente" : "Acceptée"}
                            </span>
                          </div>
                          {isInvitationPending(selectedUser) && (
                            <button
                              onClick={handleInvite}
                              className="text-xs text-primary hover:text-primary/80 font-medium px-3 py-1.5 rounded border border-primary/20 hover:bg-primary/5 transition-colors"
                            >
                              Réenvoyer l'invitation
                            </button>
                          )}
                        </div>
                        <div className="space-y-2 pt-3 border-t border-border">
                          {selectedUser.last_invite_sent_at && (
                            <div>
                              <span className="text-xs text-muted">Dernier envoi:</span>
                              <span className="text-xs text-text ml-2">
                                {new Date(selectedUser.last_invite_sent_at).toLocaleDateString("fr-FR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}
                          {selectedUser.invite_count !== undefined && selectedUser.invite_count > 0 && (
                            <div>
                              <span className="text-xs text-muted">Nombre d'invitations envoyées:</span>
                              <span className="text-xs text-text ml-2">{selectedUser.invite_count}</span>
                            </div>
                          )}
                          {selectedUser.password_changed_at && (
                            <div>
                              <span className="text-xs text-muted">Invitation acceptée le:</span>
                              <span className="text-xs text-text ml-2">
                                {new Date(selectedUser.password_changed_at).toLocaleDateString("fr-FR", {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className="text-xs text-muted mb-1 block">Statut du compte</label>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                            selectedUser.status === "active"
                              ? "bg-success/10 text-success"
                              : "bg-warning/10 text-warning"
                          }`}
                        >
                          {selectedUser.status === "active" ? "Actif" : "Suspendu"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {activeTab === "access" && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-text">Accès & permissions</h3>
                    <div className="flex gap-2">
                      {memberships.some(
                        (m) => m.organization?.type === "ambassador_org" && m.status === "active"
                      ) && (
                        <Button
                          label="Changer d'ambassadeur"
                          intent="default"
                          size="sm"
                          onClick={() => setChangeAmbassadorOpen(true)}
                        />
                      )}
                      <Button
                        label="+ Ajouter un accès"
                        intent="primary"
                        size="sm"
                        onClick={() => setAddMembershipOpen(true)}
                      />
                    </div>
                  </div>
                  <MembershipsTable
                    memberships={memberships.length > 0 ? memberships : []}
                    isSuperAdmin={selectedUser?.is_superadmin || false}
                    onEdit={(membership) => setEditingMembership(membership)}
                    onSuspend={async (membershipId) => {
                      const membership = memberships.find((m) => m.id === membershipId);
                      if (!membership) return;
                      const newStatus = membership.status === "active" ? "suspended" : "active";
                      await updateMembership(membershipId, { status: newStatus });
                      // Refresh memberships
                      const data = await listUserMemberships(selectedUser!.id);
                      setMemberships(data.items || []);
                    }}
                    onRemove={async (membershipId) => {
                      await disableMembership(membershipId);
                      // Refresh memberships
                      const data = await listUserMemberships(selectedUser!.id);
                      setMemberships(data.items || []);
                    }}
                  />
                  {/* Encart d'aide */}
                  <div className="rounded-md border border-border bg-surface-muted p-4">
                    <h4 className="text-sm font-semibold text-text mb-2">Comment ça marche ?</h4>
                    <div className="space-y-2 text-xs text-muted">
                      <p>• Un accès = un membership vers une <strong className="text-text">organisation opératrice</strong> (XLKey, Ambassadeur, Pilote).</p>
                      <p>• Le rôle définit les permissions par défaut pour cette organisation.</p>
                      <p>• Les overrides ajustent ce membership uniquement.</p>
                      <p>• Un utilisateur peut avoir plusieurs accès (ex: XLKey + Ambassadeur).</p>
                      <p className="mt-2 pt-2 border-t border-border">
                        <strong className="text-text">Gestion des clients :</strong> Par défaut, un utilisateur a accès à toutes les fermes de son organisation. La gestion des accès spécifiques aux fermes se fait dans le module Ambassadeur.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "notes" && (
                <div className="space-y-4">
                  <NotesList entityType="user" entityId={selectedUser.id} />
                </div>
              )}
              {activeTab === "history" && (
                <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-text mb-3">Historique d'activité</h3>
                    <AuditLogList
                      logs={auditLogs}
                      isLoading={isLoadingAuditLogs}
                      emptyMessage="Aucun historique disponible."
                    />
                  </div>
                </div>
              )}
              {activeTab === "settings" && (
                <div className="rounded-md border border-border bg-surface p-6 shadow-sm">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-text mb-3">Paramètres</h3>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between p-3 rounded-md border border-border bg-surface-muted">
                        <div>
                          <div className="text-sm font-medium text-text">
                            {selectedUser.password_changed_at || (selectedUser.status === "active" && !selectedUser.invited_at)
                              ? "Réinitialisation de mot de passe"
                              : "Invitation"}
                          </div>
                          <div className="text-xs text-muted mt-1">
                            {selectedUser.password_changed_at || (selectedUser.status === "active" && !selectedUser.invited_at)
                              ? "Envoyer un courriel de réinitialisation de mot de passe"
                              : "Envoyer une invitation par email"}
                          </div>
                        </div>
                        <Button 
                          label={selectedUser.password_changed_at || (selectedUser.status === "active" && !selectedUser.invited_at) ? "Réinitialiser" : "Inviter"} 
                          intent="default" 
                          size="sm" 
                          onClick={handleInvite} 
                        />
                      </div>
                      <div className="flex items-center justify-between p-3 rounded-md border border-border bg-surface-muted">
                        <div>
                          <div className="text-sm font-medium text-text">Statut du compte</div>
                          <div className="text-xs text-muted mt-1">
                            {selectedUser.status === "active" ? "Compte actif" : selectedUser.status === "suspended" ? "Compte suspendu" : "Compte archivé"}
                          </div>
                        </div>
                        <Button
                          label={selectedUser.status === "active" ? "Suspendre" : "Réactiver"}
                          intent={selectedUser.status === "active" ? "danger" : "default"}
                          size="sm"
                          onClick={handleToggleStatus}
                        />
                      </div>
                      {isSuperAdmin && (
                        <div className="flex items-center justify-between p-3 rounded-md border border-border bg-surface-muted">
                          <div>
                            <div className="text-sm font-medium text-text">Suppression définitive</div>
                            <div className="text-xs text-muted mt-1">
                              Supprimer cet utilisateur de manière définitive
                            </div>
                          </div>
                          <Button
                            label="Supprimer"
                            intent="danger"
                            size="sm"
                            onClick={() => handleDeleteUser(selectedUser.id)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-md border border-border bg-surface p-6 shadow-sm text-sm text-muted">
              Aucun utilisateur sélectionné.
            </div>
          )
        }
      />

      <Modal title="Inviter un utilisateur" open={open} onClose={handleCancel} showDefaultButtons={false} showCloseButton={true} size="md">
        <div className="space-y-4">
          <p className="text-sm text-muted">
            Remplissez les informations minimales pour envoyer une invitation. L'utilisateur complétera son profil lors de l'acceptation.
          </p>
          <div>
            <Input 
              label="Email *" 
              placeholder="prenom.nom@entreprise.com" 
              value={form.email} 
              onChange={(event) => {
                setForm({ ...form, email: event.target.value });
                if (errors.email) {
                  setErrors({ ...errors, email: "" });
                }
              }}
              state={errors.email ? "error" : "default"}
            />
            {errors.email && (
              <p className="mt-1 text-xs text-danger">{errors.email}</p>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Input 
              label="Prénom *" 
              placeholder="Prénom" 
              value={form.firstName} 
              onChange={(event) => setForm({ ...form, firstName: event.target.value })} 
            />
            <Input 
              label="Nom *" 
              placeholder="Nom" 
              value={form.lastName} 
              onChange={(event) => setForm({ ...form, lastName: event.target.value })} 
            />
          </div>
          <Select
            label="Ambassadeur"
            options={["Aucun", ...ambassadors.map((amb) => amb.name)]}
            value={form.ambassadorName}
            onChange={(value) => {
              const selected = ambassadors.find((amb) => amb.name === value);
              setForm({
                ...form,
                ambassadorName: value,
                ambassadorId: value === "Aucun" ? "" : selected?.id ?? "",
              });
            }}
          />
          <Select
            label="Rôle"
            options={ROLE_OPTIONS.map((r) => getRoleDescription(r).label)}
            value={getRoleDescription(form.role).label}
            onChange={(value) => {
              const roleValue = ROLE_OPTIONS.find((r) => getRoleDescription(r).label === value);
              setForm({ ...form, role: roleValue || "ops" });
            }}
          />
          {/* SuperAdmin checkbox removed - not needed in creation form */}
          <div className="flex justify-end gap-2 pt-2">
            <Button label="Annuler" intent="default" size="sm" onClick={handleCancel} />
            <Button label="Envoyer l'invitation" intent="primary" size="sm" onClick={handleCreate} />
          </div>
        </div>
      </Modal>

      <Drawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        title="Modifier l'utilisateur"
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Annuler" intent="secondary" size="sm" onClick={() => setEditDrawerOpen(false)} />
            <Button label="Enregistrer" intent="primary" size="sm" onClick={handleEditProfile} />
          </div>
        }
      >
        {selectedUser && (
          <div className="space-y-4">
            <Input label="Email" placeholder="prenom.nom@entreprise.com" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <div className="grid gap-3 md:grid-cols-2">
              <Input label="Prénom" placeholder="Prénom" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
              <Input label="Nom" placeholder="Nom" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
            <Input label="Téléphone" placeholder="(xxx) xxx-xxxx" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </div>
        )}
      </Drawer>

      <EditMembershipDrawer
        membership={editingMembership}
        open={!!editingMembership}
        onClose={() => setEditingMembership(null)}
        onSave={async (membershipId, updates) => {
          await updateMembership(membershipId, updates);
          // Refresh memberships
          if (selectedUser) {
            const data = await listUserMemberships(selectedUser.id);
            setMemberships(data.items || []);
          }
        }}
      />

      <AddMembershipModal
        open={addMembershipOpen}
        onClose={() => setAddMembershipOpen(false)}
        onSuccess={async () => {
          if (selectedUser) {
            const data = await listUserMemberships(selectedUser.id);
            setMemberships(data.items || []);
          }
        }}
        userId={selectedUser?.id || ""}
      />

      {selectedUser && (
        <ChangeAmbassadorFlow
          open={changeAmbassadorOpen}
          onClose={() => setChangeAmbassadorOpen(false)}
          onSuccess={async () => {
            const data = await listUserMemberships(selectedUser.id);
            setMemberships(data.items || []);
          }}
          userId={selectedUser.id}
          currentMemberships={memberships}
        />
      )}
    </AppShell>
  );
}
